import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { ISyncEngine, SyncEvent } from '@erp/native/interfaces';
import { useNativeBridge, useIsDesktop } from '@/lib/native-context';
import { createWebSyncEngine } from '@erp/native/web-adapter';
import { useDataService } from '@/lib/service-context';
import { registerSyncHandlers } from '@/lib/sync-bindings';
import { resetOfflineCounter, type CodeScope } from '@/lib/offline-counter';

interface SyncContextValue {
  engine: ISyncEngine;
  online: boolean;
  pendingCount: number;
  /**
   * Number of outbox entries in `failed` status — i.e. they've exhausted
   * MAX_ATTEMPTS and won't auto-retry. These are the "conflicts" the user
   * needs to review and resolve manually (force-retry, drop, or accept
   * the server version). Drives the "Conflits N" badge in the header.
   */
  failedCount: number;
  flushing: boolean;
  /** Last error reported by an entry, if any. */
  lastError?: string;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export function SyncProvider({ children }: { children: ReactNode }) {
  const isDesktop = useIsDesktop();
  const bridge = useNativeBridge();
  const dataService = useDataService();
  const [engine, setEngine] = useState<ISyncEngine | null>(() =>
    isDesktop ? null : createWebSyncEngine(),
  );
  const [online, setOnline] = useState(bridge.network.isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const [lastError, setLastError] = useState<string | undefined>();

  // Boot the desktop sync engine once.
  useEffect(() => {
    if (!isDesktop || engine) return;
    let cancelled = false;
    void import('@erp/native/tauri-adapter').then(async (mod) => {
      const e = await mod.createTauriSyncEngine({ network: bridge.network });
      if (cancelled) return;
      // Bind handlers so the engine knows how to translate outbox
      // entries back into IDataService calls. Safe to register more
      // than once — registerHandler overwrites by key.
      registerSyncHandlers(e, dataService);
      setEngine(e);
    });
    return () => {
      cancelled = true;
    };
  }, [isDesktop, bridge, engine, dataService]);

  // Subscribe to engine events to drive the status badge.
  useEffect(() => {
    if (!engine) return;
    let mounted = true;
    // Initial snapshot — split into "still working on it" vs "gave up".
    // pendingCount tracks active queue (pending + in_flight); failedCount
    // is the user-actionable bucket the Conflits panel surfaces.
    void engine.pending().then((entries) => {
      if (!mounted) return;
      setPendingCount(entries.filter((e) => e.status !== 'failed').length);
      setFailedCount(entries.filter((e) => e.status === 'failed').length);
    });
    const refreshCounts = () => {
      void engine.pending().then((entries) => {
        if (!mounted) return;
        setPendingCount(entries.filter((e) => e.status !== 'failed').length);
        setFailedCount(entries.filter((e) => e.status === 'failed').length);
      });
    };
    const off = engine.subscribe((event: SyncEvent) => {
      if (!mounted) return;
      switch (event.type) {
        case 'queued':
          setPendingCount((c) => c + 1);
          break;
        case 'entry_done':
          setPendingCount((c) => Math.max(0, c - 1));
          break;
        case 'entry_failed':
          setLastError(event.entry.lastError);
          if (!event.willRetry) {
            // Final failure — moves from active queue into the "Conflits"
            // bucket that the review panel surfaces.
            setPendingCount((c) => Math.max(0, c - 1));
            setFailedCount((c) => c + 1);
          }
          break;
        case 'flush_started':
          setFlushing(true);
          break;
        case 'flush_finished':
          setFlushing(false);
          // Re-snapshot from the DB rather than trusting the running
          // counters — discards / retries that happen between events
          // can leave the per-event tally out of sync with reality, and
          // a flush boundary is a natural reconcile point.
          refreshCounts();
          // Successful drain → reset offline numbering counters. The
          // server has now consumed real codes for everything that was
          // queued, so the local "+N offline offset" is no longer
          // needed. Next offline create will start fresh from the
          // server-side counter again. We only reset when nothing is
          // still pending — partial drains might leave more queued.
          if (event.failed === 0 && pendingCount === 0) {
            (['article', 'client', 'supplier', 'barcode'] as CodeScope[]).forEach(resetOfflineCounter);
          }
          break;
        case 'online':
          setOnline(true);
          break;
        case 'offline':
          setOnline(false);
          break;
        default:
          break;
      }
    });
    // Track network independently — covers the case where the engine is
    // not yet ready but we want the badge to be live.
    const offNet = bridge.network.subscribe(setOnline);
    return () => {
      mounted = false;
      off();
      offNet();
    };
  }, [engine, bridge]);

  // Cmd+R / native menu "Synchroniser maintenant" → user explicitly
  // wants to retry now. Force-flush so we bypass the offline gate
  // (network detector may be sticky-offline from earlier failures)
  // and skip exponential backoff.
  useEffect(() => {
    const handler = () => engine?.flush({ force: true });
    window.addEventListener('erp:sync:flush', handler);
    return () => window.removeEventListener('erp:sync:flush', handler);
  }, [engine]);

  if (!engine) return <>{children}</>;
  return (
    <SyncContext.Provider value={{ engine, online, pendingCount, failedCount, flushing, lastError }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync(): SyncContextValue {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
}

/** Optional: returns null when no provider is mounted (web before bootstrap). */
export function useSyncOptional(): SyncContextValue | null {
  return useContext(SyncContext);
}
